import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Key, Power, PowerOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({ registration_enabled: false });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resettingUser, setResettingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    is_admin: false,
    role: 'user',
  });
  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchSettings();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    }
    setLoading(false);
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleToggleRegistration = async (enabled) => {
    try {
      const response = await axios.put(`${API}/settings`, {
        registration_enabled: enabled,
      });
      setSettings(response.data);
      toast.success(`Cadastro ${enabled ? 'habilitado' : 'desabilitado'}!`);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || 'Erro ao atualizar configuração');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, formData);
        toast.success('Usuário atualizado!');
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success('Usuário criado!');
      }
      fetchUsers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !isActive });
      toast.success(`Usuário ${!isActive ? 'habilitado' : 'desabilitado'}!`);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este usuário?')) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success('Usuário excluído!');
      fetchUsers();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users/${resettingUser.id}/reset-password`, {
        new_password: resetPassword
      });
      toast.success('Senha resetada com sucesso!');
      setResetDialogOpen(false);
      setResettingUser(null);
      setResetPassword('');
    } catch (error) {
      toast.error('Erro ao resetar senha');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', is_admin: false });
    setEditingUser(null);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      is_admin: user.is_admin,
      role: user.role || 'user',
    });
    setDialogOpen(true);
  };

  const openResetDialog = (user) => {
    setResettingUser(user);
    setResetPassword('');
    setResetDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div data-testid="users-page">
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Usuários</h1>
          <p className="text-gray-600">Gerencie os usuários do sistema</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-user-button"
              className="bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="user-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Senha {editingUser && '(deixe em branco para não alterar)'}</Label>
                <Input
                  id="password"
                  data-testid="user-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
              </div>
              
              <div>
                <Label htmlFor="role">Tipo de Usuário</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[100]">
                    <SelectItem value="user">Usuário</SelectItem>
                    <SelectItem value="recepcao">Recepção</SelectItem>
                    <SelectItem value="cognicao">Cognição</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-3">
                <Switch
                  data-testid="user-admin-switch"
                  checked={formData.is_admin}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                />
                <Label>Administrador (antigo - usar Tipo de Usuário acima)</Label>
              </div>
              <Button
                data-testid="user-submit-button"
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
              >
                {editingUser ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Registration Settings */}
      <Card className="border-0 shadow-lg mb-6">
        <CardHeader>
          <CardTitle>Configurações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Cadastro de Usuários</p>
              <p className="text-sm text-gray-600">Permitir que novos usuários se cadastrem</p>
            </div>
            <Switch
              data-testid="registration-toggle"
              checked={settings.registration_enabled}
              onCheckedChange={handleToggleRegistration}
            />
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {users.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <p className="text-gray-500">Nenhum usuário encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => (
            <Card key={user.id} data-testid="user-card" className="border-0 shadow-lg hover:shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{user.name}</h3>
                      {user.role === 'admin' && (
                        <Badge className="bg-emerald-700 text-white">Admin</Badge>
                      )}
                      {user.role === 'recepcao' && (
                        <Badge className="bg-blue-600 text-white">Recepção</Badge>
                      )}
                      {user.role === 'cognicao' && (
                        <Badge className="bg-purple-600 text-white">Cognição</Badge>
                      )}
                      {(!user.role || user.role === 'user') && (
                        <Badge variant="outline" className="border-gray-400 text-gray-600">Usuário</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{user.email}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {user.is_active ? (
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          <Power className="w-3 h-3 mr-1" />
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-600 border-red-600">
                          <PowerOff className="w-3 h-3 mr-1" />
                          Inativo
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    data-testid="edit-user-button"
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(user)}
                    className="flex-1"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  <Button
                    data-testid="reset-password-button"
                    variant="outline"
                    size="sm"
                    onClick={() => openResetDialog(user)}
                    className="flex-1"
                  >
                    <Key className="w-3 h-3 mr-1" />
                    Reset
                  </Button>
                  <Button
                    data-testid="toggle-active-button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(user.id, user.is_active)}
                  >
                    {user.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                  </Button>
                  <Button
                    data-testid="delete-user-button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(user.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resetar Senha - {resettingUser?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new_password">Nova Senha</Label>
              <Input
                id="new_password"
                data-testid="reset-password-input"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                required
                placeholder="Digite a nova senha"
              />
            </div>
            <Button
              data-testid="reset-password-submit"
              type="submit"
              className="w-full bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
            >
              Resetar Senha
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
